import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { AvatarUploadModalComponent } from '../../../avatar-modal/avatar-upload-modal.component';

@Component({
  selector: 'servicenumber-business-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzDividerModule,
    NzGridModule,
    NzIconModule,
    NzSwitchModule,
    NzUploadModule,
    AvatarUploadModalComponent,
    NzCheckboxModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './servicenumber-businesscard.component.html',
  styleUrls: ['./servicenumber-businesscard.component.less']
})
export class ServiceBusinessCardComponent implements OnInit, OnChanges {
  @Input() serviceNumberId: string = '';

  @Input() serviceAvatarUrl: string = '';
  @Input() embeddedInDetail = false;
  serviceName: string = '';
  tenantName: string = '';

  // 添加头像上传模态框相关属性
  avatarModalVisible = false;
  isAvatarUploading = false;
  tempFile: any;

  imageUrl: any;
  businessCardForm!: FormGroup;
  loading = false;
  submitting = false;
  hasUnsavedChanges = false;
  buttonOptions: any[] = [];
  defaultButtons: string[] = []; // 存储默认选中且不可取消的按钮
  // 添加标志变量
  hasExistingCard = false;
  private baselineSnapshot = '';

  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService); // 注入 ImageService
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(ALAIN_I18N_TOKEN);

  ngOnInit() {
    this.getButtons();
    this.businessCardForm = this.createBusinessCardForm();
    if (this.serviceNumberId) {
      this.loadData();
    }
    this.setupFormValueChangeListener();
  }
  loadData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_BUSINESS_CARD_DETAIL}/${this.serviceNumberId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res?.success && res?.data) {
            this.serviceName = res.data?.serviceNumberModel?.name ?? '';
            this.tenantName = res.data?.tenantModel?.name ?? '';
            if (res.data?.businessCardModel) {
              this.businessCardForm.patchValue(res.data.businessCardModel);
              if (res.data.businessCardModel.imageId) {
                this.loadImage(res.data.businessCardModel.imageId);
              }
              this.hasExistingCard = true; // 标记已有电子名片
            }
            this.captureBaseline();
            this.cdr.detectChanges();
          }
        },
        error: error => {
          // 处理HTTP请求错误
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }
  loadImage(imageId: string): void {
    this.imageService
      .loadImage(imageId, 'M')
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(imageUrl => {
        if (imageUrl) {
          this.businessCardForm.patchValue({ imageUrl: imageUrl });
          this.imageUrl = imageUrl;
          this.captureBaseline();
          this.cdr.detectChanges();
        } else {
          console.error('Failed to load background image');
          this.businessCardForm.patchValue({ imageUrl: '' });
          this.captureBaseline();
          this.cdr.detectChanges();
        }
      });
  }

  // 修改保存方法
  saveBusinessCard(): void {
    if (this.businessCardForm.invalid) {
      // 标记所有表单控件为已触摸，以显示验证错误
      Object.values(this.businessCardForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      return;
    }

    // 其余保存逻辑保持不变
    this.submitting = true;
    const formData = this.businessCardForm.value;
    formData.serviceNumberId = this.serviceNumberId;

    // 根据是否已有电子名片决定使用创建还是更新API
    const apiUrl = this.hasExistingCard ? ConstantUtil.SERVICENUMBER_BUSINESS_CARD_UPDATE : ConstantUtil.SERVICENUMBER_BUSINESS_CARD_CREATE;

    this.http
      .postFormData(apiUrl, formData, this.tempFile, 'image')
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        // 保存逻辑保持不变
        if (res.success) {
          this.message.success(
            this.hasExistingCard
              ? this.i18n.fanyi('servicenumber.business-card.update-success')
              : this.i18n.fanyi('servicenumber.business-card.create-success')
          );
          if (!this.hasExistingCard) {
            this.hasExistingCard = true; // 创建成功后更新标志
          }
          this.captureBaseline();
        } else {
          this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.business-card.save-failed'));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessCardModel'] && changes['businessCardModel'].currentValue) {
      if (this.businessCardForm) {
        this.businessCardForm.patchValue(changes['businessCardModel'].currentValue);
        this.cdr.detectChanges();
      }
    }
  }
  getButtons() {
    this.http
      .post(ConstantUtil.BUSINESS_CARD_BUTTON_V1_LIST, {})
      .pipe(
        finalize(() => {
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res?.success) {
            const apiButtons: any[] = Array.isArray(res?.data) ? res.data : [];
            this.buttonOptions = apiButtons.map((btn: any) => ({
              label: btn?.name,
              value: btn?.code,
              disabled: !!btn?.isDefault // 默认按钮设为禁用，防止取消勾选
            }));

            // 提取默认按钮
            this.defaultButtons = apiButtons.filter((btn: any) => !!btn?.isDefault).map((btn: any) => btn?.code);

            // 更新表单中的按钮选择
            this.updateDefaultButtons();
          } else {
            this.buttonOptions = [];
            this.defaultButtons = [];
          }
        },
        error: error => {
          // 处理HTTP请求错误
          this.buttonOptions = [];
          this.defaultButtons = [];
        }
      });
  }
  // 更新默认按钮到表单
  private updateDefaultButtons() {
    if (this.businessCardForm && this.defaultButtons.length > 0) {
      const currentButtons = this.businessCardForm.get('buttons')?.value || [];
      // 合并当前选中的按钮和默认按钮
      const mergedButtons = [...new Set([...currentButtons, ...this.defaultButtons])];
      this.businessCardForm.get('buttons')?.setValue(mergedButtons);
      this.captureBaseline();
    }
  }
  private createBusinessCardForm(): FormGroup {
    return this.fb.nonNullable.group(
      {
        // 移除 useBusinessCard 字段
        useServiceAvatar: [true],
        useServiceName: [true],
        useTenantName: [true],
        useTenantPhone: [false],
        tenantPhone: [{ value: '', disabled: true }, [Validators.required]],
        useTenantAddress: [false],
        tenantAddress: [{ value: '', disabled: true }, [Validators.required]],
        useWebSite: [false],
        webSite: [{ value: '', disabled: true }, [Validators.required]],
        useOther: [false],
        other: [{ value: '', disabled: true }, [Validators.required]],
        imageId: [null],
        imageUrl: [null],
        buttons: [[]]
      },
      {
        validators: (control: AbstractControl): ValidationErrors | null => {
          const useServiceAvatar = control.get('useServiceAvatar')?.value;
          const imageUrl = control.get('imageUrl')?.value;
          const imageId = control.get('imageId')?.value;

          if (!useServiceAvatar && !imageUrl && !imageId) {
            return { noImageSelected: true };
          }

          return null;
        }
      }
    );
  }

  private setupFormValueChangeListener() {
    // 监听开关变化，控制对应输入框的启用/禁用状态
    const toggleControls = [
      { toggle: 'useTenantPhone', input: 'tenantPhone' },
      { toggle: 'useTenantAddress', input: 'tenantAddress' },
      { toggle: 'useWebSite', input: 'webSite' },
      { toggle: 'useOther', input: 'other' }
    ];

    toggleControls.forEach(control => {
      this.businessCardForm.get(control.toggle)?.valueChanges.subscribe(value => {
        if (value) {
          this.businessCardForm.get(control.input)?.enable();
        } else {
          this.businessCardForm.get(control.input)?.disable();
        }
      });
    });

    this.businessCardForm.valueChanges.subscribe(() => {
      this.recomputeUnsavedChanges();
    });

    // 监听服务号头像开关变化
    this.businessCardForm.get('useServiceAvatar')?.valueChanges.subscribe(value => {
      // 如果关闭服务号头像且没有上传背景图片，显示错误提示
      if (!value && !this.businessCardForm.get('imageUrl')?.value) {
        this.message.warning(this.i18n.fanyi('servicenumber.business-card.upload-hint'));
      }
    });
  }

  hasButton(buttonType: string): boolean {
    const buttons = this.businessCardForm.get('buttons')?.value || [];
    return buttons.includes(buttonType);
  }
  // 预览名片
  previewBusinessCard(): void {
    // 实现名片预览逻辑
    this.message.info(this.i18n.fanyi('servicenumber.business-card.preview-developing'));
  }
  // 修改打开头像上传模态框的方法
  openAvatarModal(): void {
    this.avatarModalVisible = true;
  }

  handleAvatarModalCancel(): void {
    this.avatarModalVisible = false;
  }
  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    this.tempFile = new File([event.blob], 'avatar.png', { type: 'image/png' });
    // 创建本地预览URL
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imageUrl = e.target.result;
      this.businessCardForm.patchValue({
        imageUrl: this.imageUrl
      });
      this.recomputeUnsavedChanges();
      this.avatarModalVisible = false;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(this.tempFile);
  }

  private captureBaseline(): void {
    if (!this.businessCardForm) return;
    this.businessCardForm.markAsPristine();
    this.baselineSnapshot = JSON.stringify(this.businessCardForm.getRawValue());
    this.hasUnsavedChanges = false;
  }

  private recomputeUnsavedChanges(): void {
    if (!this.businessCardForm) return;
    this.hasUnsavedChanges = JSON.stringify(this.businessCardForm.getRawValue()) !== this.baselineSnapshot;
  }
}
